import { ArrowUp, Image as ImageIcon, ImagePlus, LoaderCircle, Paperclip, Square, X } from 'lucide-react';
import { useEffect, useRef, type ClipboardEvent, type FormEvent, type KeyboardEvent } from 'react';
import { formatFileSize } from '../lib/image';
import type { ImageGenerationOptions, ModelOption, PendingImage } from '../types/chat';

type ComposerProps = {
  value: string;
  image: PendingImage | null;
  model: ModelOption | null;
  sending: boolean;
  preparingImage: boolean;
  imageMode: boolean;
  imageOptions: ImageGenerationOptions;
  disabled: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  onFile: (file: File) => void;
  onRemoveImage: () => void;
  onToggleImageMode: () => void;
  onImageOptionsChange: (options: ImageGenerationOptions) => void;
};

export function Composer(props: ComposerProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, [props.value]);

  const submit = (event: FormEvent) => { event.preventDefault(); props.onSubmit(); };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); props.onSubmit(); }
  };
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    if (props.imageMode) return;
    const item = Array.from(event.clipboardData.items).find((entry) => entry.kind === 'file' && entry.type.startsWith('image/'));
    const file = item?.getAsFile();
    if (!file) return;
    event.preventDefault();
    props.onFile(file);
  };
  const canSend = Boolean(props.value.trim() || (!props.imageMode && props.image)) && !props.disabled && !props.preparingImage;
  const updateOptions = (update: Partial<ImageGenerationOptions>) => props.onImageOptionsChange({ ...props.imageOptions, ...update });

  return (
    <div className="w-full max-w-[800px]">
      <form onSubmit={submit} className="composer-box">
        {props.imageMode && (
          <div className="mb-3 rounded-xl border border-accent/40 bg-accent-soft/40 p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-accent-hover"><ImagePlus className="size-4" />Buat gambar dengan AI</div>
            <div className="grid grid-cols-3 gap-2">
              <select value={props.imageOptions.size} onChange={(event) => updateOptions({ size: event.target.value as ImageGenerationOptions['size'] })} className="image-option-select" aria-label="Ukuran gambar">
                <option value="1024x1024">Kotak</option><option value="1536x1024">Landscape</option><option value="1024x1536">Portrait</option>
              </select>
              <select value={props.imageOptions.quality} onChange={(event) => updateOptions({ quality: event.target.value as ImageGenerationOptions['quality'] })} className="image-option-select" aria-label="Kualitas gambar">
                <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
              <select value={props.imageOptions.outputFormat} onChange={(event) => updateOptions({ outputFormat: event.target.value as ImageGenerationOptions['outputFormat'] })} className="image-option-select" aria-label="Format gambar">
                <option value="png">PNG</option><option value="jpeg">JPEG</option><option value="webp">WebP</option>
              </select>
            </div>
          </div>
        )}

        {props.image && !props.imageMode && (
          <div className="mx-1 mb-3 flex items-center gap-3 rounded-xl border border-border bg-canvas-subtle p-2.5">
            <img src={props.image.dataUrl} alt="Pratinjau lampiran" className="size-12 rounded-lg border border-border object-cover" />
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-primary">{props.image.name}</p><p className="mt-0.5 text-xs text-muted">{formatFileSize(props.image.size)} · Gambar terlampir</p></div>
            <button type="button" onClick={props.onRemoveImage} className="icon-button" aria-label="Hapus gambar"><X className="size-4" /></button>
          </div>
        )}

        <textarea ref={textareaRef} value={props.value} onChange={(event) => props.onChange(event.target.value)} onKeyDown={onKeyDown} onPaste={onPaste} rows={1} disabled={props.disabled && !props.sending} placeholder={props.imageMode ? 'Jelaskan gambar yang ingin dibuat…' : `Tulis pesan untuk ${props.model?.name || 'AI'}…`} className="max-h-40 min-h-9 w-full resize-none bg-transparent px-1 text-[15px] leading-6 text-primary outline-none placeholder:text-muted disabled:opacity-60" aria-label={props.imageMode ? 'Prompt gambar' : 'Pesan'} />

        <div className="mt-2 flex items-end justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1">
            {!props.imageMode && <><input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(event) => { const file = event.target.files?.[0]; if (file) props.onFile(file); event.currentTarget.value = ''; }} /><button type="button" onClick={() => fileRef.current?.click()} className="composer-icon" disabled={props.preparingImage} aria-label="Lampirkan gambar">{props.preparingImage ? <LoaderCircle className="size-5 animate-spin" /> : <Paperclip className="size-5" />}</button><button type="button" onClick={() => fileRef.current?.click()} className="composer-icon !hidden sm:!grid" disabled={props.preparingImage} aria-label="Pilih gambar"><ImageIcon className="size-[18px]" /></button></>}
            <button type="button" onClick={props.onToggleImageMode} className={`composer-icon ${props.imageMode ? '!bg-accent !text-white' : ''}`} aria-label="Buat gambar dengan AI" title="Buat gambar dengan AI"><ImagePlus className="size-[18px]" /></button>
            {props.image && !props.imageMode && <span className="ml-1 truncate rounded-full bg-accent-soft px-2 py-1 text-xs text-accent-hover">Vision</span>}
            {props.imageMode && <span className="ml-1 truncate rounded-full bg-accent-soft px-2 py-1 text-xs text-accent-hover">Image</span>}
          </div>
          {props.sending ? <button type="button" onClick={props.onStop} className="send-button border border-border bg-surface-active text-primary hover:bg-surface-hover" aria-label="Hentikan proses"><Square className="size-3.5 fill-current" /></button> : <button type="submit" disabled={!canSend} className="send-button bg-accent text-white hover:bg-accent-fill-hover disabled:bg-surface-active disabled:text-disabled" aria-label={props.imageMode ? 'Buat gambar' : 'Kirim pesan'}>{props.imageMode ? <ImagePlus className="size-5" /> : <ArrowUp className="size-5" />}</button>}
        </div>
      </form>
      <p className="mt-2 text-center text-[11px] text-muted max-sm:hidden">{props.imageMode ? 'Gambar dibuat oleh AI. Periksa hasil sebelum digunakan.' : 'AI dapat membuat kesalahan. Periksa informasi penting.'}</p>
    </div>
  );
}
